/**
 * Feature catalog — single source of truth for the Features mega-menu,
 * group overview pages, and all sub-feature marketing pages.
 *
 * Adding or renaming any entry here automatically updates the mega-menu,
 * breadcrumbs, and page content — nothing else needs to change.
 */

export interface SubFeature {
  slug: string;
  name: string;
  tagline: string;
  icon: string;
  headline: string;
  description: string;
  benefits: string[];
  stats?: { value: string; label: string }[];
  mockPreviewId: string;
}

export interface FeatureGroup {
  slug: string;
  name: string;
  icon: string;
  tagline: string;
  description: string;
  color: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
  gradientFrom: string;
  gradientTo: string;
  subFeatures: SubFeature[];
  /** Optional footnote rendered at the bottom of the group overview page. */
  footnote?: string;
}

export const featureCatalog: FeatureGroup[] = [
  {
    slug: "messages",
    name: "Messages",
    icon: "MessageSquare",
    tagline: "One inbox to rule them all",
    description:
      "A unified command center for every message across every channel. Vendor SMS, PadSplit Messenger, internal threads, and automated notifications — all in one place with rules-based automation so nothing falls through the cracks.",
    color: "from-teal-500 to-cyan-600",
    bgColor: "bg-teal-500/10",
    textColor: "text-teal-400",
    borderColor: "border-teal-500/30",
    gradientFrom: "#14b8a6",
    gradientTo: "#0891b2",
    subFeatures: [
      {
        slug: "vendor-sms",
        name: "Vendor SMS",
        tagline: "Text your vendors directly from PadPulse",
        icon: "Phone",
        headline: "Reach vendors by text without leaving the platform",
        description:
          "Send SMS to your vendors straight from PadPulse. Dispatch a job, share a property address, or follow up on a task by text — and keep the full conversation tied to the vendor's record so nothing gets lost.",
        benefits: [
          "Text vendors directly from their profile or a task",
          "Full SMS history logged per vendor",
          "Share job details and addresses by text",
          "Twilio-powered reliability and delivery receipts",
        ],
        stats: [
          { value: "Direct", label: "Vendor texting" },
          { value: "98%", label: "SMS open rate vs. email" },
          { value: "Logged", label: "Per-vendor history" },
        ],
        mockPreviewId: "sms",
      },
      {
        slug: "padsplit-messenger",
        name: "PadSplit Messenger",
        tagline: "PadSplit messages in your PadPulse inbox",
        icon: "MessageCircle",
        headline: "PadSplit Messenger, without the PadSplit tab",
        description:
          "Every message your members send through PadSplit Messenger appears in your PadPulse inbox automatically. Reply directly and the message is delivered via PadSplit — no need to switch tabs or apps.",
        benefits: [
          "Bi-directional PadSplit Messenger sync",
          "Reply from PadPulse — delivers via PadSplit",
          "Full conversation history with member context",
          "Rules-based auto-replies for common questions",
          "Thread-level internal notes for your team",
        ],
        stats: [
          { value: "Real-time", label: "Message sync" },
          { value: "Zero", label: "Context switching" },
          { value: "100%", label: "Bi-directional delivery" },
        ],
        mockPreviewId: "messenger",
      },
      {
        slug: "internal-threads",
        name: "Internal Threads",
        tagline: "Team context without extra apps",
        icon: "Users",
        headline: "Talk to your team inside every conversation",
        description:
          "Turn any member message into an internal discussion without leaving the thread. @mention teammates, add private notes, assign follow-ups, and keep every conversation organized — all without Slack, email threads, or sticky notes.",
        benefits: [
          "@mention teammates from any message thread",
          "Private notes invisible to members",
          "Convert any message into a task or supply order",
          "Full thread history for accountability",
          "Notification badges for unread internal threads",
        ],
        mockPreviewId: "threads",
      },
      {
        slug: "notifications",
        name: "Notifications",
        tagline: "Never miss what matters",
        icon: "Bell",
        headline: "Smart notifications that surface the right info at the right time",
        description:
          "PadPulse surfaces alerts for new member messages, maintenance updates, device health issues, payment approvals, and more — all configurable so you only get notified for what matters to your operation.",
        benefits: [
          "In-app, SMS, and email notification channels",
          "Configurable rules per notification type",
          "Batched daily or weekly digest options",
          "Device health alerts (locks, thermostats)",
          "Payment and approval notifications",
        ],
        mockPreviewId: "notifications",
      },
    ],
  },
  {
    slug: "properties",
    name: "Properties",
    icon: "Building2",
    tagline: "Manage your full portfolio in one dashboard",
    description:
      "Full property and room tracking with Zillow import, interactive Google Maps, detailed property profiles, and multi-unit support. From a single house-hack to a thousand-room portfolio — one unified dashboard.",
    color: "from-blue-500 to-cyan-600",
    bgColor: "bg-blue-500/10",
    textColor: "text-blue-400",
    borderColor: "border-blue-500/30",
    gradientFrom: "#3b82f6",
    gradientTo: "#0891b2",
    subFeatures: [
      {
        slug: "listings-status",
        name: "Listings & Status",
        tagline: "Every property status at a glance",
        icon: "Home",
        headline: "Your entire portfolio on one screen",
        description:
          "See every property you own — active, launching, under renovation, acquired, or sold — in a single filterable list. Drill into any property for full details, room status, and quick actions.",
        benefits: [
          "All properties in one filterable dashboard",
          "Per-property status (active, launching, renovation, etc.)",
          "Room-level occupancy and revenue data",
          "Quick-action buttons for common tasks",
          "Bulk operations across multiple properties",
        ],
        stats: [
          { value: "Real-time", label: "Status updates" },
          { value: "1 click", label: "To any property detail" },
          { value: "All", label: "Properties in one view" },
        ],
        mockPreviewId: "listings",
      },
      {
        slug: "map-view",
        name: "Map View",
        tagline: "See your portfolio on the map",
        icon: "Map",
        headline: "Your properties, visualized geographically",
        description:
          "An interactive Google Maps view of your entire portfolio. Click any marker to see key property stats — occupancy, revenue, and status — without leaving the map. Great for spotting geographic clusters and expansion opportunities.",
        benefits: [
          "Interactive Google Maps with property markers",
          "Clickable markers with occupancy and revenue",
          "Filter by status, occupancy range, or property group",
          "Identify expansion opportunities geographically",
          "Route planning for property visits",
        ],
        mockPreviewId: "map",
      },
      {
        slug: "zillow-import",
        name: "Zillow Import",
        tagline: "Import any listing in one click",
        icon: "Download",
        headline: "Add new properties without typing a single address",
        description:
          "Paste a Zillow listing URL and PadPulse auto-fills the property address, beds, baths, photos, and property details. One click turns a listing page into a full PadPulse property record.",
        benefits: [
          "One-click import from Zillow listing URLs",
          "Auto-fills address, beds, baths, photos",
          "Saves 10-15 minutes of data entry per property",
          "Works for any Zillow residential listing",
          "Imported data is fully editable after import",
        ],
        stats: [
          { value: "1 click", label: "To import a listing" },
          { value: "15 min", label: "Saved per property" },
          { value: "Auto", label: "Photo + detail fill" },
        ],
        mockPreviewId: "import",
      },
      {
        slug: "multi-unit",
        name: "Multi-Unit Support",
        tagline: "From one house to a thousand units",
        icon: "Building",
        headline: "Scale your portfolio without scaling your workload",
        description:
          "PadPulse handles single-family homes, duplexes, quadplexes, and large multi-unit buildings equally well. Manage all units from one account, group properties by strategy or geography, and delegate access to team members at the property-group level.",
        benefits: [
          "Single-family, duplex, quadplex and beyond",
          "Property groups for portfolio organization",
          "Team-member access scoped to specific groups",
          "Portfolio-level reporting across all units",
          "No per-unit pricing caps — grow without limits",
        ],
        stats: [
          { value: "1000+", label: "Units per account" },
          { value: "Any", label: "Unit type supported" },
          { value: "Group", label: "Based access control" },
        ],
        mockPreviewId: "multiunit",
      },
      {
        slug: "underwriting-link",
        name: "Underwriting Link",
        tagline: "From property profile straight to underwriting",
        icon: "Calculator",
        headline: "One click from property to deal analysis",
        description:
          "Every property in PadPulse links directly to its underwriting analysis. Jump from the property detail page into a pre-filled 11-section financial model — no copy-paste of addresses or details required.",
        benefits: [
          "Direct link from property to its underwriting",
          "Auto-fills property details into the model",
          "Navigate back to the property from the analysis",
          "Multiple underwriting scenarios per property",
          "Share or export underwriting reports as branded PDFs",
        ],
        mockPreviewId: "underwrlink",
      },
      {
        slug: "image-generator",
        name: "Image Generator",
        tagline: "AI-generated images for your listings",
        icon: "Sparkles",
        headline: "Generate listing and marketing images in seconds",
        description:
          "Create polished visuals for your property listings and marketing with PadPulse's built-in AI image generator. Produce staged room images and marketing graphics without a photographer or design tool.",
        benefits: [
          "AI-generated room and marketing images",
          "No photographer or design software required",
          "Generated images tied to the property record",
          "Quick iterations until the visual looks right",
        ],
        stats: [
          { value: "Seconds", label: "To generate an image" },
          { value: "AI", label: "Powered generation" },
          { value: "0", label: "Design tools needed" },
        ],
        mockPreviewId: "image-generator",
      },
    ],
  },
  {
    slug: "pipeline",
    name: "Pipeline",
    icon: "Rocket",
    tagline: "Research and underwrite before you buy",
    description:
      "Research tools for your next house: market research, buy box screening, underwriting, realtor search, and financing — every step before you buy in one organized workflow.",
    color: "from-violet-500 to-purple-600",
    bgColor: "bg-violet-500/10",
    textColor: "text-violet-400",
    borderColor: "border-violet-500/30",
    gradientFrom: "#8b5cf6",
    gradientTo: "#9333ea",
    subFeatures: [
      {
        slug: "market-research",
        name: "Market Research",
        tagline: "AI analysis of 50+ cities",
        icon: "TrendingUp",
        headline: "Find the best PadSplit markets before you buy",
        description:
          "PadPulse's AI-powered market research tool analyzes cities across the US for co-living potential — occupancy trends, average room rates, competition density, and estimated returns. Stop guessing which market to enter next.",
        benefits: [
          "AI analysis of up to 50 cities at once",
          "Occupancy rates and room rate benchmarks",
          "Competition density and saturation scoring",
          "AI-generated market narrative reports",
          "Save and compare multiple city analyses",
        ],
        stats: [
          { value: "50+", label: "Cities analyzed" },
          { value: "AI", label: "AI-powered insights" },
          { value: "Minutes", label: "vs. hours of manual research" },
        ],
        mockPreviewId: "research",
      },
      {
        slug: "buy-box",
        name: "Buy Box",
        tagline: "Automated property screening at scale",
        icon: "Search",
        headline: "Your buying criteria, automated",
        description:
          "Define your buy box criteria — price range, bed count, markets, cap rate floor — and PadPulse automatically screens properties from Zillow that match. Stop scanning listings manually; let PadPulse surface the right deals.",
        benefits: [
          "Define criteria: price, beds, market, cap rate",
          "Automated Zillow screening",
          "Saved properties for deeper analysis",
          "Free plan: 1 city scan per day (up to 100 results)",
          "Unlimited scans on every paid plan",
          "One-click underwriting from any match",
        ],
        stats: [
          { value: "Auto", label: "Property screening" },
          { value: "1/day", label: "Free scans · unlimited when paid" },
          { value: "1 click", label: "To full underwriting" },
        ],
        mockPreviewId: "buybox",
      },
      {
        slug: "underwrite",
        name: "Underwrite",
        tagline: "11-section financial analysis with PDF export",
        icon: "ClipboardList",
        headline: "Know the numbers before you make an offer",
        description:
          "PadPulse's underwriting tool covers all 11 sections of a PadSplit deal: income, expenses, financing, NOI, cap rate, cash-on-cash return, and more. Export a branded PDF to share with lenders or partners in seconds.",
        benefits: [
          "11-section PadSplit-specific financial model",
          "Per-room income and vacancy assumptions",
          "Financing scenarios with multiple loan structures",
          "One-click LTR comp import from Rentcast",
          "Branded PDF export for lenders and partners",
        ],
        stats: [
          { value: "11", label: "Sections of analysis" },
          { value: "PDF", label: "Branded export" },
          { value: "1 click", label: "LTR comp import" },
        ],
        mockPreviewId: "underwrite",
      },
      {
        slug: "acquisitions",
        name: "Acquisitions",
        tagline: "Track every deal from offer to close",
        icon: "KeyRound",
        headline: "Your full deal pipeline in one place",
        description:
          "Manage every deal in your acquisitions pipeline across 9 stages — from initial interest through offer, inspection, financing, and closing. List view or Kanban board, your choice.",
        benefits: [
          "9-stage acquisition pipeline",
          "List view and Kanban board modes",
          "Document uploads per deal (contract, inspection, etc.)",
          "Notes, contacts, and timeline per property",
          "Link to underwriting analysis from any deal card",
        ],
        mockPreviewId: "acquisitions",
      },
      {
        slug: "find-realtor",
        name: "Find a Realtor",
        tagline: "Connect with PadSplit-experienced agents",
        icon: "UserCheck",
        headline: "Work with realtors who understand co-living",
        description:
          "Browse a curated directory of realtors who specialize in PadSplit-ready properties. Filter by market, experience level, and specialty — then reach out directly from PadPulse.",
        benefits: [
          "Curated directory of PadSplit-experienced realtors",
          "Filter by market and specialty",
          "Direct contact from within PadPulse",
          "Realtor profiles with market focus and reviews",
          "Submit your own realtor for listing",
        ],
        mockPreviewId: "realtor",
      },
      {
        slug: "finance",
        name: "Finance",
        tagline: "PadSplit-friendly lenders in one place",
        icon: "Landmark",
        headline: "Find lenders who understand PadSplit deals",
        description:
          "A curated directory of lenders who have financed PadSplit co-living properties. Browse by loan type and market — then connect directly through PadPulse.",
        benefits: [
          "Lenders with PadSplit co-living experience",
          "Filter by loan type (DSCR, conventional, etc.)",
          "Direct inquiry from within PadPulse",
          "Save favorite lenders to your profile",
        ],
        mockPreviewId: "finance",
      },
      {
        slug: "buy",
        name: "Buy",
        tagline: "Active deals from hosts and wholesalers",
        icon: "ShoppingBag",
        headline: "A marketplace of investment-ready co-living deals",
        description:
          "Browse active deal opportunities from wholesalers and fellow PadPulse hosts. Every listing includes the address, asking price, deal summary, and a direct link to run an underwriting analysis before you commit.",
        benefits: [
          "Verified deal listings from vetted wholesalers",
          "Host-to-host marketplace for active properties",
          "One-click underwriting for any listed deal",
          "Direct in-app messaging with sellers",
          "Save deals to your watchlist",
        ],
        mockPreviewId: "buy",
      },
      {
        slug: "renovate",
        name: "Renovate",
        tagline: "Budget, track, and manage renovations",
        icon: "Hammer",
        headline: "Keep renovations on time and on budget",
        description:
          "Plan renovation scopes, track budgets room-by-room, log contractor updates, and manage phase timelines — all tied to the property record so your renovation history is always accessible.",
        benefits: [
          "Per-room renovation scope and budget tracking",
          "Phase-based timeline management",
          "Contractor assignment and update logging",
          "Budget vs. actual cost comparisons",
          "Photo documentation per renovation phase",
        ],
        mockPreviewId: "renovate",
      },
      {
        slug: "furnish",
        name: "Furnish",
        tagline: "Room-by-room furnishing tracker",
        icon: "Sofa",
        headline: "Get every room furnishing-ready for PadSplit launch",
        description:
          "Track furnishing orders room-by-room, monitor delivery status, and ensure every room meets PadSplit's furnishing standards before launch. The recommended furnishings catalog gives you a head start on sourcing.",
        benefits: [
          "Room-by-room furnishing checklist",
          "Order tracking with delivery status",
          "Recommended furnishings catalog (curated for PadSplit)",
          "Budget tracking per room",
          "Mark rooms furnishing-complete for launch readiness",
        ],
        mockPreviewId: "furnish",
      },
      {
        slug: "launch",
        name: "Launch",
        tagline: "Pre-launch checklist to go-live on PadSplit",
        icon: "Rocket",
        headline: "Go live on PadSplit with nothing missed",
        description:
          "PadPulse's property launch wizard walks you through every step required to activate a property on PadSplit — from listing details and photos to room configurations and the final go-live submission.",
        benefits: [
          "Step-by-step PadSplit launch checklist",
          "VA-written listing descriptions",
          "Room configuration with photo uploads",
          "Pre-launch compliance check against PadSplit standards",
          "One-click go-live submission to PadSplit",
        ],
        stats: [
          { value: "VA", label: "Listing descriptions" },
          { value: "100%", label: "Launch checklist coverage" },
          { value: "1 click", label: "Go-live submission" },
        ],
        mockPreviewId: "launch",
      },
    ],
  },
  {
    slug: "members",
    name: "Members",
    icon: "Users",
    tagline: "Know every member, manage every relationship",
    description:
      "Complete member lifecycle management. PadSplit members sync automatically into PadPulse — with full profiles, vehicle tracking, and booking request management all in one place.",
    color: "from-emerald-500 to-green-600",
    bgColor: "bg-emerald-500/10",
    textColor: "text-emerald-400",
    borderColor: "border-emerald-500/30",
    gradientFrom: "#10b981",
    gradientTo: "#16a34a",
    subFeatures: [
      {
        slug: "members",
        name: "Members",
        tagline: "Full member profiles synced from PadSplit",
        icon: "User",
        headline: "Every member, every detail, in one place",
        description:
          "PadSplit members sync automatically into PadPulse with full profiles — payment history, move-in/out dates, stay records, message threads, maintenance interactions, and internal notes all attached to one member record.",
        benefits: [
          "Auto-sync from PadSplit — no manual data entry",
          "Full stay history per member",
          "Internal notes and tags per member profile",
          "Search and filter across all members",
          "Link members to rooms and properties instantly",
        ],
        stats: [
          { value: "Auto", label: "PadSplit sync" },
          { value: "Full", label: "Stay history per member" },
          { value: "Instant", label: "Member search" },
        ],
        mockPreviewId: "members",
      },
      {
        slug: "vehicles",
        name: "Vehicles",
        tagline: "Vehicle and parking registry for every member",
        icon: "Car",
        headline: "Know who drives what, instantly",
        description:
          "Log every member's vehicles — make, model, color, license plate — and look up any car in seconds. No more combing through PadSplit or asking the same question twice when a neighbor calls about a car in their spot.",
        benefits: [
          "Per-member vehicle registry (make, model, plate)",
          "Instant plate-to-member lookup",
          "Parking spot assignment per vehicle",
          "One-click from any vehicle to message the member",
          "Search vehicles across your entire portfolio",
        ],
        mockPreviewId: "vehicles",
      },
      {
        slug: "booking-requests",
        name: "Booking Requests",
        tagline: "Manage member booking requests with ease",
        icon: "ClipboardList",
        headline: "Handle booking requests without the back-and-forth",
        description:
          "Prospective members can submit booking requests directly from your PadPulse booking form. Review applications, approve or decline, and manage the full intake pipeline from one dashboard.",
        benefits: [
          "Custom booking request form for your properties",
          "Application review dashboard with status tracking",
          "Approve, decline, or request more info inline",
          "Document and ID upload support",
          "Automated confirmation and next-step emails",
        ],
        mockPreviewId: "booking",
      },
      {
        slug: "reviews",
        name: "Reviews",
        tagline: "CSAT and move-in review visibility",
        icon: "Star",
        headline: "See how members rate your properties",
        description:
          "PadPulse surfaces PadSplit CSAT and move-in review scores per property, aggregated and filterable. An AI-powered 30-day summary narrative highlights patterns, flags problem properties, and notifies you when a bad review comes in.",
        benefits: [
          "CSAT and move-in review scores per property",
          "AI 30-day summary narrative",
          "Bad-review push notifications",
          "Historical review trend charts",
          "Property-level and portfolio-level views",
        ],
        stats: [
          { value: "AI", label: "30-day review summary" },
          { value: "Instant", label: "Bad-review alerts" },
          { value: "All", label: "PadSplit reviews surfaced" },
        ],
        mockPreviewId: "reviews",
      },
    ],
  },
  {
    slug: "operations",
    name: "Operations",
    icon: "Wrench",
    tagline: "Full ops suite for co-living at scale",
    description:
      "Maintenance Kanban, automated task rules, service forms, member cleaning schedules, team to-dos, supply management, dynamic pricing, and smart device control — everything you need to run a professional co-living operation from one dashboard.",
    color: "from-orange-500 to-red-500",
    bgColor: "bg-orange-500/10",
    textColor: "text-orange-400",
    borderColor: "border-orange-500/30",
    gradientFrom: "#f97316",
    gradientTo: "#ef4444",
    subFeatures: [
      {
        slug: "tasks",
        name: "Tasks",
        tagline: "Maintenance Kanban synced with PadSplit",
        icon: "ClipboardCheck",
        headline: "Visual task management that talks to PadSplit",
        description:
          "Run every maintenance issue end-to-end — from problem identification all the way to vendor payment — on one board. Tickets created in PadSplit appear here automatically and sync back bi-directionally. Assign a vendor, track the work, approve the completed job, and pay the vendor without ever leaving the task.",
        benefits: [
          "End-to-end flow: problem identification → vendor payment",
          "Approve completed work and pay the vendor in one click",
          "Bi-directional PadSplit maintenance sync",
          "Task Rules: recurring tasks and form sends spawn automatically on a schedule",
          "Auto-route tasks to the right vendor by type",
          "Photo and document attachments per task",
        ],
        stats: [
          { value: "End-to-end", label: "Problem to vendor payment" },
          { value: "2-way", label: "PadSplit sync" },
          { value: "1 click", label: "Approve & pay vendor" },
        ],
        mockPreviewId: "tasks",
      },
      {
        slug: "service-forms",
        name: "Service Forms",
        tagline: "Digital forms with photo uploads and signatures",
        icon: "FileText",
        headline: "No more juggling third-party forms",
        description:
          "Create custom service forms for move-ins, inspections, repairs, and more — with photo upload, geolocation capture, and digital signature support built in. Set recurring service forms that auto-send to your vendors on a schedule — landscaping, cleaning, and trash — so routine work dispatches itself. Share a link; get a timestamped, GPS-tagged, signed submission back.",
        benefits: [
          "Custom form builder for any service type",
          "Recurring sends powered by Task Rules (landscaping, cleaning, trash — daily to bi-annually)",
          "Photo upload with GPS geolocation tagging",
          "Digital signature capture",
          "Timestamped, audit-ready submissions",
          "Link forms to properties, tasks, or vendors",
        ],
        mockPreviewId: "forms",
      },
      {
        slug: "cleaning-schedule",
        name: "Cleaning Program",
        tagline: "Member self-cleaning on autopilot",
        icon: "Sparkles",
        headline: "Your members clean the house. PadPulse runs the schedule.",
        description:
          "Shared spaces only stay clean when everyone knows exactly what's theirs to do — and someone follows up. PadPulse generates each property's cleaning schedule straight from live occupancy, reminds every member of their turn by text or PadSplit message, and collects photo proof when the work is done. Pick the model that fits each house: a Daily Duty Roster gives every member a standing day of the week (spread evenly, so Tuesday's member count matches Thursday's), while a Weekly Whole-House Rotation hands the entire house to one bedroom per week, rotating room by room. Move-ins join the schedule automatically; move-outs drop off without leaving holes.",
        benefits: [
          "Daily Duty Roster: every member gets a standing weekday, distributed evenly across the week",
          "Weekly Rotation: each bedroom owns the whole house for a week, rotating automatically",
          "Schedules generate from live occupancy — move-ins and move-outs handled for you",
          "Automatic reminders by SMS or PadSplit message, with photo proof forms",
          "Portfolio and per-property calendars with color-coded completion status",
          "Overlay third-party cleaner visits and dispatch them to your vendors",
        ],
        stats: [
          { value: "2 models", label: "Daily roster or weekly rotation" },
          { value: "Auto", label: "Reminders + photo proof" },
          { value: "Live", label: "Occupancy-driven schedules" },
        ],
        mockPreviewId: "cleaning-schedule",
      },
      {
        slug: "todos",
        name: "To-Dos",
        tagline: "The team checklist that runs your week",
        icon: "ClipboardList",
        headline: "Every 'don't forget to…' in one accountable list",
        description:
          "Not everything is a vendor job. To-Dos is the lightweight checklist for you and your team — assign an owner, attach a property, set a due date, and watch overdue items flag themselves. New PadPulse accounts even start with a guided onboarding checklist so you always know your next step.",
        benefits: [
          "Assign to-dos to yourself or any team member",
          "Attach a property and a due date to every item",
          "Overdue items flag automatically",
          "Full done history — who finished what, and when",
          "New accounts start with a guided onboarding checklist",
        ],
        mockPreviewId: "todos",
      },
      {
        slug: "supplies",
        name: "Supplies",
        tagline: "Track supply orders and stay stocked",
        icon: "Package",
        headline: "Never run out of the essentials",
        description:
          "Set up recurring supply orders for each property — cleaning products, toiletries, maintenance consumables — and PadPulse tracks usage, flags when you're running low, and reminds you to reorder before you run out.",
        benefits: [
          "Per-property recurring supply order setup",
          "Usage tracking and low-stock alerts",
          "Reorder reminders before you run dry",
          "Supply cost tracking tied to financials",
          "Bulk supply management across portfolio",
        ],
        mockPreviewId: "supplies",
      },
      {
        slug: "trash-service",
        name: "Trash Service",
        tagline: "On-demand trash pickup, managed in PadPulse",
        icon: "Trash2",
        headline: "Co-living trash problems, solved",
        description:
          "PadPulse integrates a white-label trash collection service built for the realities of co-living. Enroll a property in weekly can-to-curb service starting at $50 per property per month — we take the bins to the curb on collection day and bring them back. Schedule on-demand pickups, track service history, and handle bulk item disposal, all from the Operations dashboard.",
        benefits: [
          "Enroll properties in weekly can-to-curb service",
          "Starting at $50 per property per month",
          "On-demand trash pickup scheduling",
          "Bulk item and furniture disposal",
          "Service history per property, in the Operations dashboard",
        ],
        stats: [
          { value: "$50/mo", label: "Per property, to start" },
          { value: "Weekly", label: "Can-to-curb service" },
          { value: "On-demand", label: "Extra pickups" },
        ],
        mockPreviewId: "trash",
      },
      {
        slug: "pricing-rooms",
        name: "Pricing / Rooms",
        tagline: "Dynamic room pricing that pushes to PadSplit",
        icon: "DollarSign",
        headline: "Set it and forget it room pricing",
        description:
          "PadPulse's dynamic pricing engine sets and pushes room rates to PadSplit automatically. Configure occupancy-based rules, end-of-month pricing adjustments, and per-room pricing strategies — then let PadPulse do the repricing work.",
        benefits: [
          "Occupancy-based repricing rules",
          "End-of-month rate adjustments for last-minute demand",
          "Per-room strategy (fill-rate vs. profit optimization)",
          "Auto-calculates weekly and monthly rates",
          "Pushes directly to PadSplit with no manual entry",
        ],
        stats: [
          { value: "10%", label: "Avg revenue increase" },
          { value: "Auto", label: "Rate pushes to PadSplit" },
          { value: "Per room", label: "Pricing strategies" },
        ],
        mockPreviewId: "pricing",
      },
      {
        slug: "smart-locks",
        name: "Smart Locks",
        tagline: "Remote lock control and member access codes",
        icon: "Lock",
        headline: "Keyless entry for every room and every member",
        description:
          "Control TTLock, Sifely, and other compatible smart locks from PadPulse — so you never have to log into your lock system again. Generate time-limited member access codes, grant vendor access for scheduled work, and monitor battery health with automatic member notifications for low-battery alerts.",
        benefits: [
          "Remote lock / unlock from anywhere",
          "Per-member access codes with expiry",
          "Time-limited vendor access codes",
          "Automatic member notifications for low-battery alerts",
          "Never log into your lock system again",
          "TTLock & Sifely integrate free; other locks connect through Seam for $2 a month per lock",
        ],
        stats: [
          { value: "Remote", label: "Lock control" },
          { value: "Time-limited", label: "Vendor codes" },
          { value: "Auto", label: "Low-battery alerts" },
        ],
        mockPreviewId: "locks",
      },
      {
        slug: "thermostats",
        name: "Thermostats",
        tagline: "HVAC monitoring across every property",
        icon: "Thermometer",
        headline: "Temperature monitoring without the property visit",
        description:
          "Monitor thermostat temperatures and HVAC health across every property from a single dashboard. Get alerted to anomalies — a thermostat that's too hot or too cold — before they become a maintenance call. PadPulse connects to the brands you already own: Honeywell, Ecobee, Sensi, Google, and Nest.",
        benefits: [
          "Live temperature monitoring per property",
          "HVAC health alerts and anomaly detection",
          "Temperature thresholds configurable per property",
          "Works with Honeywell, Ecobee, Sensi, Google, and Nest",
          "Historical temperature trend logging",
        ],
        stats: [
          { value: "5+", label: "Thermostat brands supported" },
          { value: "Live", label: "Temperature monitoring" },
          { value: "Auto", label: "Anomaly alerts" },
        ],
        mockPreviewId: "thermostats",
      },
    ],
  },
  {
    slug: "vendors",
    name: "Vendors",
    icon: "Briefcase",
    tagline: "Manage every vendor relationship in one place",
    description:
      "A full vendor CRM, payment system, and SMS portal — so every vendor relationship lives in PadPulse, not scattered across texts, emails, and spreadsheets.",
    color: "from-amber-500 to-yellow-500",
    bgColor: "bg-amber-500/10",
    textColor: "text-amber-400",
    borderColor: "border-amber-500/30",
    gradientFrom: "#f59e0b",
    gradientTo: "#eab308",
    subFeatures: [
      {
        slug: "vendor-directory",
        name: "Vendor Directory",
        tagline: "Your full vendor CRM in PadPulse",
        icon: "Users",
        headline: "Your entire vendor roster, organized",
        description:
          "Add vendors to your PadPulse directory — plumbers, electricians, cleaners, handymen — with full contact details, trade specialties, service areas, and performance history. Find the right vendor for any job in seconds.",
        benefits: [
          "Full vendor profiles with trade and service area",
          "Performance history and task completion rates",
          "Assign vendors to tasks and properties",
          "Internal notes and tags per vendor",
          "Search and filter by trade or market",
        ],
        mockPreviewId: "directory",
      },
      {
        slug: "vendor-marketplace",
        name: "Vendor Marketplace",
        tagline: "Find PadSplit-ready vendors in your market",
        icon: "Store",
        headline: "New market? Find vendors who know co-living",
        description:
          "Browse a community marketplace of vendors who have worked on PadSplit properties. Read reviews from fellow hosts, compare pricing, and onboard new vendors directly to your PadPulse account.",
        benefits: [
          "Community-reviewed vendors for co-living properties",
          "Browse by trade, market, and availability",
          "Host reviews from real PadPulse operators",
          "One-click vendor onboarding from marketplace to directory",
          "Submit your own trusted vendors to the marketplace",
        ],
        mockPreviewId: "marketplace",
      },
      {
        slug: "vendor-payments",
        name: "Vendor Payments",
        tagline: "Approve and pay vendors without leaving PadPulse",
        icon: "CreditCard",
        headline: "Pay vendors fast, with a full audit trail",
        description:
          "When a vendor marks a task complete, it enters your approval queue. Review the work, rate the vendor, and approve payment — all from one screen. Payments are processed via Stripe Connect for fast, direct vendor payouts.",
        benefits: [
          "Task-completion-triggered approval workflow",
          "Vendor rating on every completed job",
          "Bulk approval for multiple tasks",
          "Stripe Connect direct-to-vendor payouts (3% processing fee)",
          "Full payment history and audit trail",
        ],
        stats: [
          { value: "Same day", label: "Vendor payouts" },
          { value: "Bulk", label: "Approval queue" },
          { value: "Full", label: "Payment audit trail" },
        ],
        mockPreviewId: "payments",
      },
      {
        slug: "vendor-settings",
        name: "Vendor Settings",
        tagline: "Configure your vendor portal and routing",
        icon: "Settings",
        headline: "Set up your vendor workflow once, run it forever",
        description:
          "Configure task routing rules, set default vendors per trade and property, customize the vendor portal experience, and manage vendor onboarding invitations — all from the vendor settings panel.",
        benefits: [
          "Auto-routing rules: route tasks by trade and property",
          "Default vendor assignments per property",
          "Vendor portal invite management",
          "SMS notification preferences per vendor",
          "Auto-pay vendors — never pay a vendor on the wrong card again",
          "Auto-match task → property → payment card",
        ],
        mockPreviewId: "vendorsettings",
      },
    ],
  },
  {
    slug: "revenue",
    name: "Revenue",
    icon: "DollarSign",
    tagline: "Full financial suite for co-living operators",
    description:
      "Profit & Loss, income tracking, expense management, Mercury statement sync, forwarded document handling, direct bookings marketing, and third-party listing management — everything your co-living financials need.",
    color: "from-green-500 to-emerald-600",
    bgColor: "bg-green-500/10",
    textColor: "text-green-400",
    borderColor: "border-green-500/30",
    gradientFrom: "#22c55e",
    gradientTo: "#059669",
    subFeatures: [
      {
        slug: "profit-loss",
        name: "Profit & Loss",
        tagline: "Real-time P&L across your entire portfolio",
        icon: "BarChart3",
        headline: "Know your numbers — property by property and portfolio-wide",
        description:
          "PadPulse's P&L dashboard shows income, expenses, and net operating income in real time — filterable by property, date range, and category. Drill down to individual transactions or zoom out to the portfolio view.",
        benefits: [
          "Real-time P&L by property and portfolio",
          "Income vs. expense category breakdowns",
          "Date-range filtering (month, quarter, year)",
          "Drill-down to individual transaction level",
          "Mercury Bank transaction auto-import",
        ],
        stats: [
          { value: "Real-time", label: "P&L updates" },
          { value: "Per property", label: "And portfolio views" },
          { value: "Auto", label: "Mercury import" },
        ],
        mockPreviewId: "pl",
      },
      {
        slug: "income",
        name: "Income",
        tagline: "Track every dollar coming in",
        icon: "TrendingUp",
        headline: "Every income source, categorized and searchable",
        description:
          "Log and track all income sources — rent payments, booking fees, application fees, and more. Income entries auto-import from Mercury Bank and are categorized automatically for clean P&L reporting.",
        benefits: [
          "Auto-import from Mercury Bank",
          "Income categorization (rent, fees, other)",
          "Per-property and portfolio income views",
          "Monthly and annual income trend charts",
        ],
        mockPreviewId: "income",
      },
      {
        slug: "expenses",
        name: "Expenses",
        tagline: "Every expense, one place",
        icon: "Receipt",
        headline: "Expense tracking that actually works for landlords",
        description:
          "Log expenses per property — repairs, utilities, insurance, management fees. Auto-import from Mercury Bank, categorize by type, and watch your expense report build itself.",
        benefits: [
          "Manual and bank-imported expense tracking",
          "Auto-import from Mercury Bank transactions",
          "Category-based expense classification",
          "Per-property and portfolio expense views",
          "Bulk edit and categorization tools",
        ],
        mockPreviewId: "expenses",
      },
      {
        slug: "collections",
        name: "Collections",
        tagline: "Track and recover outstanding member balances",
        icon: "AlertCircle",
        headline: "Stop losing money to unpaid balances",
        description:
          "PadPulse's collections module tracks members with outstanding balances, lets you set threshold parameters, and surfaces actionable collection lists so you always know who owes what — and by how much.",
        benefits: [
          "Outstanding balance tracking per member",
          "Configurable threshold parameters ($100 to -$299)",
          "Exportable collections report",
          "Notes and action logging per collection record",
        ],
        stats: [
          { value: "Custom", label: "Balance thresholds" },
          { value: "Live", label: "Outstanding balances" },
          { value: "Export", label: "Collections report" },
        ],
        mockPreviewId: "collections",
      },
      {
        slug: "statements",
        name: "Statements",
        tagline: "Mercury statements, synced automatically",
        icon: "FileText",
        headline: "Your bank statements, downloaded for you",
        description:
          "PadPulse automatically syncs and downloads your Mercury Bank statements every cycle, so your financial records are always complete and ready for reconciliation or tax time — no manual downloads required.",
        benefits: [
          "Automatic sync with Mercury statements",
          "Automatic download of Mercury statements each cycle",
          "Statements attached to your financial records",
          "Always ready for reconciliation and tax time",
          "Searchable statement archive",
        ],
        mockPreviewId: "docs",
      },
      {
        slug: "direct-bookings",
        name: "Direct Bookings",
        tagline: "Your own booking site — bypass PadSplit fees",
        icon: "Globe",
        headline: "Save $250–$300 per booking with your own booking site",
        description:
          "PadPulse creates a verified, branded booking website for your properties that builds trust and converts inquiries directly — bypassing PadSplit's 10-day booking fees entirely. Auto-syncs room availability so your site is always current.",
        benefits: [
          "Branded booking site with your logo and colors",
          "Auto-synced room availability",
          "Lead capture with contact information",
          "Save $250–$300 per booking vs. PadSplit fees",
          "Verified host badge for trust and credibility",
        ],
        stats: [
          { value: "$250–$300", label: "Saved per booking" },
          { value: "Auto", label: "Availability sync" },
          { value: "Branded", label: "Your own domain" },
        ],
        mockPreviewId: "directbookings",
      },
      {
        slug: "third-party-listings",
        name: "Third-Party Listings",
        tagline: "Manage listings across every channel",
        icon: "ExternalLink",
        headline: "Every listing platform, one dashboard",
        description:
          "Track and manage your room listings across Zillow, Facebook Marketplace, Roomies, HotPads, SpareRoom, Furnished Finders, and more — from one bird's-eye view. Reminders, pricing tracking, and channel coverage all in one place.",
        benefits: [
          "Multi-platform listing tracking (Zillow, Facebook, Roomies, more)",
          "Bird's-eye channel coverage view",
          "Listing refresh reminders",
          "Pricing consistency tracking across platforms",
          "Custom listing notes per channel",
        ],
        mockPreviewId: "listings3p",
      },
      {
        slug: "sell",
        name: "Sell",
        tagline: "List your properties for sale to other hosts",
        icon: "Tag",
        headline: "Exit to the community, not a stranger",
        description:
          "List your PadPulse-managed properties for sale on the host-to-host marketplace. Every listing is verified, includes live property data, and connects you directly with interested buyers through in-app deal chat.",
        benefits: [
          "Host-to-host co-living property marketplace",
          "Verified listings with live property data",
          "In-app deal chat with prospective buyers",
          "Set asking price and deal terms",
          "Exposure to a community of active co-living investors",
        ],
        mockPreviewId: "sell",
      },
    ],
  },
  {
    slug: "connections",
    name: "Connections",
    icon: "Plug",
    tagline: "Deep integrations with every tool you already use",
    description:
      "PadPulse connects to your PadSplit account, Stripe, Mercury Bank, TTLock, Sifely, and more — so your data flows automatically between the systems you already use, with no manual re-keying required.",
    color: "from-indigo-500 to-blue-600",
    bgColor: "bg-indigo-500/10",
    textColor: "text-indigo-400",
    borderColor: "border-indigo-500/30",
    gradientFrom: "#6366f1",
    gradientTo: "#2563eb",
    subFeatures: [
      {
        slug: "padsplit-sync",
        name: "PadSplit Sync",
        tagline: "Bi-directional PadSplit integration",
        icon: "RefreshCw",
        headline: "PadSplit and PadPulse, always in sync",
        description:
          "PadPulse maintains a continuous bi-directional sync with your PadSplit account. Members, rooms, maintenance tickets, messages, and pricing all flow between the two platforms automatically — no manual updates required.",
        benefits: [
          "Automated bi-directional sync engine",
          "Members, rooms, and maintenance synced",
          "Pricing changes push directly to PadSplit",
          "Messages synced to and from PadSplit Messenger",
          "Bulk financial imports",
        ],
        stats: [
          { value: "100%", label: "Bi-directional sync" },
          { value: "Real-time", label: "Data updates" },
          { value: "Zero", label: "Manual re-keying" },
        ],
        mockPreviewId: "padsplit",
      },
      {
        slug: "stripe",
        name: "Stripe",
        tagline: "Billing and vendor payments via Stripe",
        icon: "CreditCard",
        headline: "Stripe powers everything financial in PadPulse",
        description:
          "PadPulse uses Stripe for subscription billing and Stripe Connect for vendor payouts — giving you a single, trusted payment infrastructure for all financial flows in the platform.",
        benefits: [
          "Stripe-powered subscription billing",
          "Stripe Connect for direct vendor payouts",
          "PCI-compliant payment processing",
          "Automatic proration on plan changes",
          "Full payment history in your Stripe dashboard",
        ],
        mockPreviewId: "stripe",
      },
      {
        slug: "mercury-bank",
        name: "Mercury Bank",
        tagline: "Auto-import transactions from Mercury",
        icon: "Landmark",
        headline: "Your Mercury transactions in PadPulse automatically",
        description:
          "Connect your Mercury Bank account securely. Every transaction — debit, credit, transfer — appears in PadPulse automatically, pre-categorized as income or expense for instant P&L updates.",
        benefits: [
          "Automatic transaction import",
          "HMAC-SHA256 signature verification for security",
          "Transactions categorized as income or expense",
          "Instant P&L updates with every import",
          "Per-host Mercury connection — no shared credentials",
        ],
        stats: [
          { value: "Auto", label: "Transaction import" },
          { value: "Instant", label: "P&L update" },
          { value: "Secure", label: "HMAC-secured" },
        ],
        mockPreviewId: "mercury",
      },
      {
        slug: "ttlock-sifely",
        name: "TTLock & Sifely",
        tagline: "TTLock and Sifely smart lock platforms",
        icon: "Lock",
        headline: "Control TTLock and Sifely locks from PadPulse",
        description:
          "PadPulse integrates natively with both TTLock and Sifely smart lock platforms via their respective APIs. Connect your developer account, sync your locks, and manage access codes — all from the PadPulse devices dashboard.",
        benefits: [
          "Full TTLock Open Platform API integration",
          "Full API integration for both platforms",
          "Guided in-app connection wizard for both platforms",
          "Access code management from PadPulse",
          "Webhook-based real-time lock event logging",
        ],
        mockPreviewId: "ttlock",
      },
    ],
  },
];

/** Look up a group by slug. Returns undefined if not found. */
export function getGroupBySlug(slug: string): FeatureGroup | undefined {
  return featureCatalog.find((g) => g.slug === slug);
}

/** Look up a sub-feature by group slug + sub-feature slug. */
export function getSubFeatureBySlug(
  groupSlug: string,
  featureSlug: string,
): { group: FeatureGroup; subFeature: SubFeature } | undefined {
  const group = getGroupBySlug(groupSlug);
  if (!group) return undefined;
  const subFeature = group.subFeatures.find((f) => f.slug === featureSlug);
  if (!subFeature) return undefined;
  return { group, subFeature };
}

const SITE_URL = "https://padpulse.io";

type JsonLd = Record<string, unknown>;

function breadcrumbList(crumbs: { name: string; url: string }[]): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: c.url,
    })),
  };
}

/**
 * Structured data (JSON-LD) for a feature group overview page:
 * a breadcrumb trail (Home › Features › Group) plus an ItemList of the
 * group's sub-features so Google can surface richer results.
 */
export function buildGroupJsonLd(group: FeatureGroup): JsonLd[] {
  const groupUrl = `${SITE_URL}/features/${group.slug}`;
  return [
    breadcrumbList([
      { name: "Home", url: SITE_URL },
      { name: "Features", url: `${SITE_URL}/features` },
      { name: group.name, url: groupUrl },
    ]),
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: `${group.name} features`,
      description: group.description,
      itemListElement: group.subFeatures.map((sf, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: sf.name,
        description: sf.tagline,
        url: `${groupUrl}/${sf.slug}`,
      })),
    },
  ];
}

/**
 * Structured data (JSON-LD) for the features index page (/features): a
 * breadcrumb trail (Home › Features) plus an ItemList of every feature group
 * so Google can surface the catalog as a richer result.
 */
export function buildFeaturesIndexJsonLd(): JsonLd[] {
  return [
    breadcrumbList([
      { name: "Home", url: SITE_URL },
      { name: "Features", url: `${SITE_URL}/features` },
    ]),
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "PadPulse feature groups",
      description:
        "Every PadPulse feature group — messaging, property management, acquisitions, member management, operations, vendors, revenue, and integrations.",
      itemListElement: featureCatalog.map((group, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: group.name,
        description: group.tagline,
        url: `${SITE_URL}/features/${group.slug}`,
      })),
    },
  ];
}

/**
 * The capabilities Daily Pulse monitors — title + description shown on the
 * marketing page's "Everything Pulse monitors" grid. Single source of truth so
 * the page render, the client `useSEO` JSON-LD, and the server-side SEO
 * prerender all draw the same `featureList` and can never drift.
 */
export const dailyPulseCapabilities: { title: string; description: string }[] = [
  {
    title: "Inbox & Messages",
    description:
      "Flags unanswered member messages, long-waiting threads, and communication gaps before they affect your PadSplit response rating.",
  },
  {
    title: "Booking Requests",
    description:
      "Surfaces pending booking requests, tells you which ones have submitted their form, and how long each has been waiting.",
  },
  {
    title: "Tasks & Maintenance",
    description:
      "Reports open maintenance tasks, vendor acceptance status, and anything overdue that could affect member satisfaction.",
  },
  {
    title: "Acquisitions Pipeline",
    description:
      "Watches your deal pipeline for critical deadlines — inspection cutoffs, closing windows, financing contingencies.",
  },
  {
    title: "Smart Devices",
    description:
      "Checks thermostat setpoints vs. actual readings, lock status anomalies, and any device health issues across your portfolio.",
  },
  {
    title: "Reviews & CSAT",
    description:
      "Alerts you to new negative reviews, patterns in member feedback, and anything that could signal a satisfaction issue.",
  },
  {
    title: "Financial Signals",
    description:
      "Highlights revenue anomalies, outstanding collections, and anything unexpected in your financial picture.",
  },
  {
    title: "Team Oversight",
    description:
      "Catches things your team members might have missed — Pulse is always watching, even when you aren't.",
  },
];

/**
 * Structured data (JSON-LD) for the Daily Pulse feature page
 * (/features/daily-pulse): a breadcrumb trail (Home › Features › Daily Pulse)
 * plus a SoftwareApplication entry carrying the page's capability list.
 *
 * Defaults to the shared `dailyPulseCapabilities` titles so the server-side
 * prerender and client render stay identical without passing the list around.
 */
export function buildDailyPulseJsonLd(
  featureList: string[] = dailyPulseCapabilities.map((c) => c.title),
): JsonLd[] {
  const url = `${SITE_URL}/features/daily-pulse`;
  return [
    breadcrumbList([
      { name: "Home", url: SITE_URL },
      { name: "Features", url: `${SITE_URL}/features` },
      { name: "Daily Pulse", url },
    ]),
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Daily Pulse — PadPulse",
      description:
        "Daily Pulse reads your entire PadSplit operation every morning and tells you exactly what needs your attention — a personalized spoken briefing that catches what your team might miss.",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      url,
      featureList,
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
      },
      publisher: {
        "@type": "Organization",
        name: "PadPulse",
        url: SITE_URL,
      },
    },
  ];
}

/**
 * Structured data (JSON-LD) for a sub-feature page: a breadcrumb trail
 * (Home › Features › Group › Feature) plus a SoftwareApplication entry
 * carrying the feature's name, description, and benefit list.
 */
export function buildSubFeatureJsonLd(
  group: FeatureGroup,
  subFeature: SubFeature,
): JsonLd[] {
  const featureUrl = `${SITE_URL}/features/${group.slug}/${subFeature.slug}`;
  return [
    breadcrumbList([
      { name: "Home", url: SITE_URL },
      { name: "Features", url: `${SITE_URL}/features` },
      { name: group.name, url: `${SITE_URL}/features/${group.slug}` },
      { name: subFeature.name, url: featureUrl },
    ]),
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: `${subFeature.name} — PadPulse`,
      description: subFeature.description,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      url: featureUrl,
      featureList: subFeature.benefits,
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
      },
      publisher: {
        "@type": "Organization",
        name: "PadPulse",
        url: SITE_URL,
      },
    },
  ];
}
